import React, { useState } from 'react';
import {
  Container,
  Tab,
  Nav,
  Button,
  Alert,
  Row,
  Col,
  Form as BsForm,
} from 'react-bootstrap';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import HeaderAzul from '~/Components/HeaderAzul/HeaderAzul';
import Loading from '~/Components/Loading';
import TabelaDinamica from '~/Components/TabelaDinamica/Tabela';
import { listarRelatorioVariantesAtivo } from '~/Services/Service';
import { salvarEmExcel } from '~/Utils/Helpers';

const CODIGO_MAX = 999999999;
const NOME_MAX_SENTINELA = 'Z'.repeat(30);
const NOME_MIN_SENTINELA = ' ';

const INITIAL_VALUES = {
  codigoInicial: '',
  codigoFinal: '',
  nomeInicial: '',
  nomeFinal: '',
  abaAtiva: 'codigo',
};

const schema = Yup.object().shape({
  abaAtiva: Yup.string().oneOf(['codigo', 'nome']).required(),

  codigoInicial: Yup.number()
    .nullable()
    .transform((value, original) =>
      String(original).trim() === '' ? null : value
    )
    .typeError('Código inicial deve ser numérico')
    .integer('Código inicial deve ser inteiro')
    .positive('Código inicial deve ser positivo'),

  codigoFinal: Yup.number()
    .nullable()
    .transform((value, original) =>
      String(original).trim() === '' ? null : value
    )
    .typeError('Código final deve ser numérico')
    .integer('Código final deve ser inteiro')
    .positive('Código final deve ser positivo')
    .test(
      'codigo-final-gte-inicial',
      'Código final deve ser maior ou igual ao inicial',
      function(value) {
        const { codigoInicial } = this.parent;
        if (value == null || codigoInicial == null || codigoInicial === '')
          return true;
        return value >= Number(codigoInicial);
      }
    ),

  nomeInicial: Yup.string()
    .nullable()
    .max(30, 'Nome inicial tem no máximo 30 caracteres'),

  nomeFinal: Yup.string()
    .nullable()
    .max(30, 'Nome final tem no máximo 30 caracteres')
    .test(
      'nome-final-gte-inicial',
      'Nome final deve ser maior ou igual ao inicial',
      function(value) {
        const { nomeInicial } = this.parent;
        if (!value || !nomeInicial) return true;
        return value.localeCompare(nomeInicial) >= 0;
      }
    ),
});

const colunas = [
  {
    key: 'codigo_variante',
    title: 'Código',
    width: '20%',
    align: 'left',
    ordenacao: true,
  },
  {
    key: 'nome_variante',
    title: 'Nome da Variante',
    width: '80%',
    align: 'left',
    ordenacao: true,
  },
];

const montarParams = (values) => {
  if (values.abaAtiva === 'codigo') {
    const ini = String(values.codigoInicial).trim();
    const fim = String(values.codigoFinal).trim();
    if (ini === '' && fim === '') return { modoFiltro: 'TodosPorCodigo' };
    return {
      modoFiltro: 'IntervaloCodigo',
      codigoInicial: ini === '' ? 0 : Number(ini),
      codigoFinal: fim === '' ? CODIGO_MAX : Number(fim),
    };
  }
  const ini = String(values.nomeInicial).trim();
  const fim = String(values.nomeFinal).trim();
  if (ini === '' && fim === '') return { modoFiltro: 'TodosPorNome' };
  return {
    modoFiltro: 'IntervaloNome',
    nomeInicial: ini === '' ? NOME_MIN_SENTINELA : ini,
    nomeFinal: fim === '' ? NOME_MAX_SENTINELA : fim,
  };
};

const RelatorioVariantesAtivoPage = () => {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [exportando, setExportando] = useState(false);

  const handleSubmit = async (values, { setSubmitting }) => {
    const params = montarParams(values);

    setLoading(true);
    setErro(null);
    setDados(null);

    try {
      const res = await listarRelatorioVariantesAtivo(params);
      setDados(res.dados || []);
    } catch (err) {
      const mensagem =
        err && err.message ? err.message : 'Erro ao carregar relatório.';
      setErro(mensagem);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleExportar = async () => {
    if (!dados || dados.length === 0) return;
    setExportando(true);
    try {
      await salvarEmExcel(dados, '/exportador/excel/relatorio-variantes-ativo');
    } catch (err) {
      toast.error('Erro ao exportar Excel.');
    } finally {
      setExportando(false);
    }
  };

  return (
    <Container fluid className="px-3 py-3">
      <HeaderAzul title="Relatório de Variantes de Ativos" />

      <Formik
        initialValues={INITIAL_VALUES}
        validationSchema={schema}
        onSubmit={handleSubmit}
        enableReinitialize={false}
      >
        {({ values, setFieldValue, isSubmitting, submitForm }) => {
          const handleTabChange = (key) => {
            if (key === values.abaAtiva) return;
            if (values.abaAtiva === 'codigo') {
              setFieldValue('codigoInicial', '');
              setFieldValue('codigoFinal', '');
            } else {
              setFieldValue('nomeInicial', '');
              setFieldValue('nomeFinal', '');
            }
            setFieldValue('abaAtiva', key);
            setDados(null);
            setErro(null);
          };

          return (
            <Form>
              <Tab.Container
                activeKey={values.abaAtiva}
                onSelect={handleTabChange}
              >
                <Nav variant="tabs" className="mb-0">
                  <Nav.Item>
                    <Nav.Link eventKey="codigo">Por Código</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="nome">Por Nome</Nav.Link>
                  </Nav.Item>
                </Nav>

                <div className="border border-top-0 rounded-bottom p-3 mb-3">
                  <Tab.Content>
                    <Tab.Pane eventKey="codigo">
                      <Row className="align-items-end">
                        <Col xs={12} md={3}>
                          <BsForm.Group controlId="codigoInicial">
                            <BsForm.Label className="small mb-1">
                              Código inicial
                            </BsForm.Label>
                            <Field
                              name="codigoInicial"
                              type="number"
                              className="form-control form-control-sm"
                              placeholder="Ex.: 1"
                            />
                            <ErrorMessage
                              name="codigoInicial"
                              component="div"
                              className="text-danger small"
                            />
                          </BsForm.Group>
                        </Col>
                        <Col xs={12} md={3}>
                          <BsForm.Group controlId="codigoFinal">
                            <BsForm.Label className="small mb-1">
                              Código final
                            </BsForm.Label>
                            <Field
                              name="codigoFinal"
                              type="number"
                              className="form-control form-control-sm"
                              placeholder="Ex.: 99"
                            />
                            <ErrorMessage
                              name="codigoFinal"
                              component="div"
                              className="text-danger small"
                            />
                          </BsForm.Group>
                        </Col>
                        <Col xs={12} md={3}>
                          <Button
                            variant="primary"
                            size="sm"
                            className="mb-3"
                            onClick={submitForm}
                            disabled={isSubmitting || loading}
                          >
                            Gerar relatório
                          </Button>
                        </Col>
                      </Row>
                    </Tab.Pane>

                    <Tab.Pane eventKey="nome">
                      <Row className="align-items-end">
                        <Col xs={12} md={3}>
                          <BsForm.Group controlId="nomeInicial">
                            <BsForm.Label className="small mb-1">
                              Nome inicial
                            </BsForm.Label>
                            <Field
                              name="nomeInicial"
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Ex.: PADRÃO"
                              maxLength={30}
                            />
                            <ErrorMessage
                              name="nomeInicial"
                              component="div"
                              className="text-danger small"
                            />
                          </BsForm.Group>
                        </Col>
                        <Col xs={12} md={3}>
                          <BsForm.Group controlId="nomeFinal">
                            <BsForm.Label className="small mb-1">
                              Nome final
                            </BsForm.Label>
                            <Field
                              name="nomeFinal"
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Ex.: POPULAR "
                              maxLength={30}
                            />
                            <ErrorMessage
                              name="nomeFinal"
                              component="div"
                              className="text-danger small"
                            />
                          </BsForm.Group>
                        </Col>
                        <Col xs={12} md={3}>
                          <Button
                            variant="primary"
                            size="sm"
                            className='mb-3'
                            onClick={submitForm}
                            disabled={isSubmitting || loading}
                          >
                            Gerar relatório
                          </Button>
                        </Col>
                      </Row>
                    </Tab.Pane>
                  </Tab.Content>
                </div>
              </Tab.Container>

              {dados !== null && dados.length > 0 && (
                <div className="mb-2 text-right">
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleExportar}
                    disabled={exportando}
                  >
                    {exportando ? 'Exportando...' : 'Exportar Excel'}
                  </Button>
                </div>
              )}

              {loading && <Loading label="Carregando relatório..." />}

              {!loading && erro && (
                <Alert variant="danger">{erro}</Alert>
              )}

              {!loading && dados === null && (
                <p className="text-muted mt-3">
                  Clique em &apos;Gerar relatório&apos; para carregar os dados (campos vazios trazem todos os registros).
                </p>
              )}

              {!loading && dados !== null && dados.length === 0 && (
                <Alert variant="info">
                  Nenhuma variante encontrada para o filtro informado.
                </Alert>
              )}

              {!loading && dados !== null && dados.length > 0 && (
                <div className="table-responsive">
                  <TabelaDinamica
                    colunas={colunas}
                    dados={dados.map((item) => ({
                      ...item,
                      id: item.codigo_variante,
                    }))}
                  />
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
};

export default RelatorioVariantesAtivoPage;
